import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiSave, FiSend, FiPlus, FiTrash2, FiCreditCard, FiCalendar,
  FiUser, FiPackage, FiPercent, FiFileText, FiX, FiCheck,
  FiChevronDown, FiChevronUp, FiDownload, FiEye, FiArrowLeft
} from 'react-icons/fi';
import { format, addDays } from 'date-fns';
import { toast } from 'react-toastify';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { v4 as uuidv4 } from 'uuid';
import { useCurrency } from '../../contexts/CurrencyContext';
import styles from './QuoteCreate.module.css';

interface QuoteForm {
  quoteNumber: string;
  clientId: string;
  issueDate: Date;
  validUntil: Date;
  items: QuoteItemForm[];
  subtotal: number;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  taxRate: number;
  total: number;
  notes: string;
  terms: string;
  status: string;
}

interface QuoteItemForm {
  id: string;
  productId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  taxRate: number;
}

const mockClients: Client[] = [
  {
    id: '1',
    name: 'Acme Corporation',
    email: 'contact@acme.com',
    phone: '+1 (555) 123-4567',
    address: {
      street: '123 Business St',
      city: 'New York',
      state: 'NY',
      zipCode: '10001',
      country: 'USA'
    }
  },
  {
    id: '2',
    name: 'Tech Solutions Inc',
    email: 'info@techsolutions.com',
    phone: '+1 (555) 987-6543',
    address: {
      street: '456 Innovation Ave',
      city: 'San Francisco',
      state: 'CA',
      zipCode: '94102',
      country: 'USA'
    }
  },
  {
    id: '3',
    name: 'HutsTech',
    email: 'segodi@hutstech.co.za',
    phone: '+27 82 826 8596',
    address: {
      street: '17 Reitz Road President Park AH',
      city: 'Halfway House',
      state: 'Gauteng',
      zipCode: '1685',
      country: 'South Africa'
    }
  }
];

const mockProducts: Product[] = [
  {
    id: '1',
    name: 'Basic Website',
    description: 'Professional basic website with responsive design',
    price: 1499,
    taxRate: 15
  },
  {
    id: '2',
    name: 'Standard Website',
    description: 'Advanced website with custom features and CMS',
    price: 2499,
    taxRate: 15
  },
  {
    id: '3',
    name: 'Corporate Pack',
    description: 'Premium corporate website with advanced functionality',
    price: 3499,
    taxRate: 15
  },
  {
    id: '4',
    name: 'Starter eCommerce',
    description: 'Basic eCommerce website with essential features',
    price: 5499,
    taxRate: 15
  },
  {
    id: '5',
    name: 'Standard eCommerce',
    description: 'Full-featured eCommerce platform with advanced tools',
    price: 7999,
    taxRate: 15
  },
  {
    id: '6',
    name: 'Hosting',
    description: 'Monthly web hosting service with SSL and support',
    price: 439,
    taxRate: 15
  },
  {
    id: '7',
    name: 'Domain .co.za',
    description: 'South African domain registration (.co.za)',
    price: 129,
    taxRate: 15
  },
  {
    id: '8',
    name: 'Domain .com',
    description: 'International domain registration (.com)',
    price: 189,
    taxRate: 15
  },
  {
    id: '9',
    name: 'Domain .org',
    description: 'Organization domain registration (.org)',
    price: 199,
    taxRate: 15
  },
  {
    id: '10',
    name: 'Domain .org.za',
    description: 'South African organization domain (.org.za)',
    price: 165,
    taxRate: 15
  }
];

const clientOptions = mockClients.map(client => ({
  value: client.id,
  label: client.name,
  data: client
}));

const productOptions = mockProducts.map(product => ({
  value: product.id,
  label: product.name,
  data: product
}));

// Mock quote data for editing
const mockQuote: QuoteForm = {
  quoteNumber: 'Q-2024-001',
  clientId: '1',
  issueDate: new Date(),
  validUntil: addDays(new Date(), 30),
  items: [
    {
      id: '1',
      productId: '1',
      description: 'Custom website development services',
      quantity: 1,
      unitPrice: 2500,
      total: 2500
    },
    {
      id: '2',
      productId: '3',
      description: 'IT consulting and strategy',
      quantity: 10,
      unitPrice: 150,
      total: 1500
    }
  ],
  subtotal: 4000,
  discountType: 'percentage',
  discountValue: 10,
  taxRate: 8.5,
  total: 3906,
  notes: 'This quote is valid for 30 days from the issue date.',
  terms: 'Quotation Terms and Conditions\n\nBy accepting this quotation, the client agrees to the stated pricing, scope, and validity period. Any changes requested after acceptance may incur additional charges. Payment terms, unless otherwise specified, are due within 5 days from the invoice date. The provider reserves the right to revise pricing if the project scope changes or if there are unforeseen cost increases.',
  status: 'draft'
};

export function QuoteEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatCurrencySimple } = useCurrency();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSendOptions, setShowSendOptions] = useState(false);

  const { register, control, handleSubmit, watch, setValue, formState: { errors, isValid } } = useForm<QuoteForm>({
    defaultValues: mockQuote
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const watchedItems = watch('items');
  const watchedDiscountType = watch('discountType');
  const watchedDiscountValue = watch('discountValue');
  const watchedTaxRate = watch('taxRate');

  const subtotal = watchedItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  const discountAmount = watchedDiscountType === 'percentage' ? (subtotal * watchedDiscountValue / 100) : watchedDiscountValue;
  const taxableAmount = subtotal - discountAmount;
  const taxAmount = taxableAmount * (watchedTaxRate / 100);
  const total = taxableAmount + taxAmount;

  useEffect(() => {
    // Simulate loading quote data
    const loadQuote = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Set form values from loaded quote
      setValue('quoteNumber', mockQuote.quoteNumber);
      setValue('clientId', mockQuote.clientId);
      setValue('issueDate', mockQuote.issueDate);
      setValue('validUntil', mockQuote.validUntil);
      setValue('items', mockQuote.items);
      setValue('discountType', mockQuote.discountType);
      setValue('discountValue', mockQuote.discountValue);
      setValue('taxRate', mockQuote.taxRate);
      setValue('notes', mockQuote.notes);
      setValue('terms', mockQuote.terms);
      setValue('status', mockQuote.status);

      // Set selected client
      const client = mockClients.find(c => c.id === mockQuote.clientId);
      if (client) {
        setSelectedClient(client);
      }

      setIsLoading(false);
    };

    loadQuote();
  }, [id, setValue]);

  const handleClientChange = (option: any) => {
    if (option) {
      setSelectedClient(option.data);
      setValue('clientId', option.value);
    } else {
      setSelectedClient(null);
      setValue('clientId', '');
    }
  };

  const handleProductChange = (index: number, option: any) => {
    if (option) {
      const product = option.data;
      setValue(`items.${index}.productId`, product.id);
      setValue(`items.${index}.description`, product.description);
      setValue(`items.${index}.unitPrice`, product.price);
      setValue(`items.${index}.total`, product.price * watchedItems[index].quantity);
    }
  };

  const addItem = () => {
    append({
      id: uuidv4(),
      productId: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    });
  };

  const removeItem = (index: number) => {
    remove(index);
  };

  const onSubmit = async (data: QuoteForm) => {
    try {
      setIsSaving(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Updated quote:', data);
      toast.success('Quote updated successfully!');
      navigate(`/quotes/${id}`);
    } catch (error) {
      toast.error('Failed to update quote');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendQuote = () => {
    setShowSendOptions(true);
  };

  const handleSendLink = async () => {
    try {
      setIsSending(true);
      setShowSendOptions(false);
      // Simulate API call to generate and send link
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Quote link sent successfully!');
      navigate(`/quotes/${id}`);
    } catch (error) {
      toast.error('Failed to send quote link');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendCopy = async () => {
    try {
      setIsSending(true);
      setShowSendOptions(false);
      // Simulate API call to send copy
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Quote copy sent successfully!');
      navigate(`/quotes/${id}`);
    } catch (error) {
      toast.error('Failed to send quote copy');
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading quote...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className={styles.container} 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button 
            onClick={() => navigate(`/quotes/${id}`)}
            className={styles.backButton}
          >
            <FiArrowLeft size={20} />
            Back to Quote
          </button>
          <h1 className={styles.title}>Edit Quote</h1>
          <p className={styles.subtitle}>Update quote details and items</p>
        </div>
        
        <div className={styles.headerActions}>
          <button
            onClick={() => setIsPreviewMode(!isPreviewMode)}
            className={`${styles.actionButton} ${styles.previewButton}`}
          >
            {isPreviewMode ? <FiX size={16} /> : <FiEye size={16} />}
            {isPreviewMode ? 'Hide Preview' : 'Preview'}
          </button>
          
          <button
            onClick={handleSendQuote}
            disabled={isSending || !isValid}
            className={`${styles.actionButton} ${styles.sendButton} ${isSending ? styles.loading : ''}`}
          >
            <FiSend size={16} />
            {isSending ? 'Sending...' : 'Send Quote'}
          </button>
          
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={isSaving || !isValid}
            className={`${styles.actionButton} ${styles.saveButton} ${isSaving ? styles.loading : ''}`}
          >
            <FiSave size={16} />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className={styles.content}>
        {/* Form Section */}
        <div className={`${styles.formSection} ${isPreviewMode ? styles.hidden : ''}`}>
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            {/* Quote Details */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Quote Details</h2>
              
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Quote Number</label>
                  <input
                    {...register('quoteNumber', { required: 'Quote number is required' })}
                    className={`${styles.input} ${errors.quoteNumber ? styles.error : ''}`}
                    placeholder="Q-2024-001"
                  />
                  {errors.quoteNumber && (
                    <span className={styles.errorMessage}>{errors.quoteNumber.message}</span>
                  )}
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.label}>Status</label>
                  <select
                    {...register('status', { required: 'Status is required' })}
                    className={`${styles.select} ${errors.status ? styles.error : ''}`}
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                    <option value="expired">Expired</option>
                  </select>
                  {errors.status && (
                    <span className={styles.errorMessage}>{errors.status.message}</span>
                  )}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Issue Date</label>
                  <DatePicker
                    selected={watch('issueDate')}
                    onChange={(date) => setValue('issueDate', date || new Date())}
                    className={styles.dateInput}
                    dateFormat="MMM dd, yyyy"
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.label}>Valid Until</label>
                  <DatePicker
                    selected={watch('validUntil')}
                    onChange={(date) => setValue('validUntil', date || addDays(new Date(), 30))}
                    className={styles.dateInput}
                    dateFormat="MMM dd, yyyy"
                    minDate={new Date()}
                  />
                </div>
              </div>
            </div>

            {/* Client Information */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Client Information</h2>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Client</label>
                <Select
                  value={clientOptions.find(option => option.value === watch('clientId'))}
                  onChange={handleClientChange}
                  options={clientOptions}
                  className={styles.select}
                  placeholder="Select a client"
                  isClearable
                />
              </div>

              {selectedClient && (
                <div className={styles.clientInfo}>
                  <div className={styles.clientDetails}>
                    <div className={styles.clientDetail}>
                      <span className={styles.detailLabel}>Email:</span>
                      <span className={styles.detailValue}>{selectedClient.email}</span>
                    </div>
                    <div className={styles.clientDetail}>
                      <span className={styles.detailLabel}>Phone:</span>
                      <span className={styles.detailValue}>{selectedClient.phone}</span>
                    </div>
                    <div className={styles.clientDetail}>
                      <span className={styles.detailLabel}>Address:</span>
                      <span className={styles.detailValue}>
                        {selectedClient.address.street}, {selectedClient.address.city}, {selectedClient.address.state} {selectedClient.address.zipCode}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Items */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Items</h2>
                <button
                  type="button"
                  onClick={addItem}
                  className={styles.addItemButton}
                >
                  <FiPlus size={16} />
                  Add Item
                </button>
              </div>

              <div className={styles.itemsContainer}>
                {fields.map((field, index) => (
                  <div key={field.id} className={styles.itemRow}>
                    <div className={styles.itemHeader}>
                      <span className={styles.itemNumber}>Item {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className={styles.removeItemButton}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>

                    <div className={styles.itemContent}>
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Product/Service</label>
                        <Select
                          value={productOptions.find(option => option.value === watchedItems[index]?.productId)}
                          onChange={(option) => handleProductChange(index, option)}
                          options={productOptions}
                          className={styles.select}
                          placeholder="Select a product or service"
                        />
                      </div>

                      <div className={styles.formGroup}>
                        <label className={styles.label}>Description</label>
                        <textarea
                          {...register(`items.${index}.description` as const, { required: 'Description is required' })}
                          className={`${styles.textarea} ${errors.items?.[index]?.description ? styles.error : ''}`}
                          placeholder="Item description"
                          rows={3}
                        />
                      </div>

                      <div className={styles.itemRow}>
                        <div className={styles.formGroup}>
                          <label className={styles.label}>Quantity</label>
                          <input
                            type="number"
                            {...register(`items.${index}.quantity` as const, { 
                              required: 'Quantity is required',
                              min: { value: 1, message: 'Quantity must be at least 1' }
                            })}
                            className={`${styles.input} ${errors.items?.[index]?.quantity ? styles.error : ''}`}
                            min="1"
                            step="1"
                          />
                        </div>

                        <div className={styles.formGroup}>
                          <label className={styles.label}>Unit Price</label>
                          <div className={styles.inputWrapper}>
                            <FiCreditCard className={styles.inputIcon} />
                            <input
                              type="number"
                              {...register(`items.${index}.unitPrice` as const, { 
                                required: 'Unit price is required',
                                min: { value: 0, message: 'Unit price must be positive' }
                              })}
                              className={`${styles.input} ${errors.items?.[index]?.unitPrice ? styles.error : ''}`}
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>

                        <div className={styles.formGroup}>
                          <label className={styles.label}>Total</label>
                          <div className={styles.inputWrapper}>
                            <FiCreditCard className={styles.inputIcon} />
                            <input
                              type="number"
                              value={(watchedItems[index]?.quantity || 0) * (watchedItems[index]?.unitPrice || 0)}
                              className={styles.input}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Totals</h2>
              
              <div className={styles.totalsGrid}>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Subtotal:</span>
                  <span className={styles.totalValue}>{formatCurrencySimple(subtotal)}</span>
                </div>

                <div className={styles.discountSection}>
                  <div className={styles.discountType}>
                    <label className={styles.label}>Discount Type</label>
                    <select
                      {...register('discountType')}
                      className={styles.select}
                    >
                      <option value="percentage">Percentage (%)</option>
                                              <option value="fixed">Fixed Amount (R)</option>
                    </select>
                  </div>

                  <div className={styles.discountValue}>
                    <label className={styles.label}>Discount Value</label>
                    <div className={styles.inputWrapper}>
                      {watchedDiscountType === 'percentage' ? (
                        <FiPercent className={styles.inputIcon} />
                      ) : (
                        <FiCreditCard className={styles.inputIcon} />
                      )}
                      <input
                        type="number"
                        {...register('discountValue', { 
                          min: { value: 0, message: 'Discount must be positive' }
                        })}
                        className={`${styles.input} ${errors.discountValue ? styles.error : ''}`}
                        min="0"
                        step={watchedDiscountType === 'percentage' ? '1' : '0.01'}
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Discount Amount:</span>
                  <span className={styles.totalValue}>-{formatCurrencySimple(discountAmount)}</span>
                </div>

                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Taxable Amount:</span>
                  <span className={styles.totalValue}>{formatCurrencySimple(taxableAmount)}</span>
                </div>

                <div className={styles.taxSection}>
                  <label className={styles.label}>Tax Rate (%)</label>
                  <div className={styles.inputWrapper}>
                    <FiPercent className={styles.inputIcon} />
                    <input
                      type="number"
                      {...register('taxRate', { 
                        min: { value: 0, message: 'Tax rate must be positive' }
                      })}
                      className={`${styles.input} ${errors.taxRate ? styles.error : ''}`}
                      min="0"
                      step="0.1"
                    />
                  </div>
                </div>

                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Tax Amount:</span>
                  <span className={styles.totalValue}>{formatCurrencySimple(taxAmount)}</span>
                </div>

                <div className={`${styles.totalRow} ${styles.grandTotal}`}>
                  <span className={styles.totalLabel}>Total:</span>
                  <span className={styles.totalValue}>{formatCurrencySimple(total)}</span>
                </div>
              </div>
            </div>

            {/* Notes and Terms */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Additional Information</h2>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Notes</label>
                <textarea
                  {...register('notes')}
                  className={styles.textarea}
                  placeholder="Additional notes for the client"
                  rows={4}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Terms & Conditions</label>
                <textarea
                  {...register('terms')}
                  className={styles.textarea}
                  placeholder="Payment terms and conditions"
                  rows={4}
                />
              </div>
            </div>
          </form>
        </div>

        {/* Preview Panel */}
        <AnimatePresence>
          {isPreviewMode && (
            <motion.div
              className={styles.previewPanel}
              initial={{ opacity: 0, x: 300 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 300 }}
              transition={{ duration: 0.3 }}
            >
              <div className={styles.previewHeader}>
                <h3 className={styles.previewTitle}>Quote Preview</h3>
                <button
                  onClick={() => setIsPreviewMode(false)}
                  className={styles.closePreviewButton}
                >
                  <FiX size={20} />
                </button>
              </div>

              <div className={styles.previewContent}>
                <div className={styles.previewQuote}>
                  <div className={styles.previewHeader}>
                    <div className={styles.previewCompany}>
                      <h2>C-Band</h2>
                      <p>Professional Invoicing & Accounting</p>
                    </div>
                    <div className={styles.previewQuoteInfo}>
                      <h3>QUOTE</h3>
                      <p><strong>Quote #:</strong> {watch('quoteNumber')}</p>
                      <p><strong>Date:</strong> {watch('issueDate') ? format(watch('issueDate'), 'MMM dd, yyyy') : ''}</p>
                      <p><strong>Valid Until:</strong> {watch('validUntil') ? format(watch('validUntil'), 'MMM dd, yyyy') : ''}</p>
                    </div>
                  </div>

                  {selectedClient && (
                    <div className={styles.previewClient}>
                      <h4>Bill To:</h4>
                      <p><strong>{selectedClient.name}</strong></p>
                      <p>{selectedClient.address.street}</p>
                      <p>{selectedClient.address.city}, {selectedClient.address.state} {selectedClient.address.zipCode}</p>
                      <p>{selectedClient.email}</p>
                      <p>{selectedClient.phone}</p>
                    </div>
                  )}

                  <div className={styles.previewItems}>
                    <table className={styles.previewTable}>
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Unit Price</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {watchedItems.map((item, index) => (
                          <tr key={index}>
                            <td>{item.description}</td>
                            <td>{item.quantity}</td>
                                                <td>{formatCurrencySimple(item.unitPrice)}</td>
                    <td>{formatCurrencySimple(item.quantity * item.unitPrice)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className={styles.previewTotals}>
                    <div className={styles.previewTotalRow}>
                      <span>Subtotal:</span>
                      <span>{formatCurrencySimple(subtotal)}</span>
                    </div>
                    <div className={styles.previewTotalRow}>
                      <span>Discount:</span>
                      <span>-{formatCurrencySimple(discountAmount)}</span>
                    </div>
                    <div className={styles.previewTotalRow}>
                      <span>Tax ({watchedTaxRate}%):</span>
                      <span>{formatCurrencySimple(taxAmount)}</span>
                    </div>
                    <div className={`${styles.previewTotalRow} ${styles.previewGrandTotal}`}>
                      <span>Total:</span>
                      <span>{formatCurrencySimple(total)}</span>
                    </div>
                  </div>

                  {watch('notes') && (
                    <div className={styles.previewNotes}>
                      <h4>Notes:</h4>
                      <p>{watch('notes')}</p>
                    </div>
                  )}

                  {watch('terms') && (
                    <div className={styles.previewTerms}>
                      <h4>Terms & Conditions:</h4>
                      <p>{watch('terms')}</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Send Options Modal */}
        <AnimatePresence>
          {showSendOptions && (
            <motion.div
              className={styles.modalOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div
                className={styles.modal}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ duration: 0.2 }}
              >
                <div className={styles.modalHeader}>
                  <h3 className={styles.modalTitle}>Send Quote</h3>
                  <button
                    onClick={() => setShowSendOptions(false)}
                    className={styles.modalCloseButton}
                  >
                    <FiX size={20} />
                  </button>
                </div>
                
                <div className={styles.modalContent}>
                  <p className={styles.modalDescription}>
                    Choose how you would like to send this quote:
                  </p>
                  
                  <div className={styles.sendOptions}>
                    <button
                      onClick={handleSendLink}
                      disabled={isSending}
                      className={`${styles.sendOptionButton} ${styles.sendLinkButton}`}
                    >
                      <FiSend size={16} />
                      <div className={styles.sendOptionContent}>
                        <span className={styles.sendOptionTitle}>Send Link</span>
                        <span className={styles.sendOptionDescription}>
                          Send a secure link to the client to view the quote online
                        </span>
                      </div>
                    </button>
                    
                    <button
                      onClick={handleSendCopy}
                      disabled={isSending}
                      className={`${styles.sendOptionButton} ${styles.sendCopyButton}`}
                    >
                      <FiSend size={16} />
                      <div className={styles.sendOptionContent}>
                        <span className={styles.sendOptionTitle}>Send Copy</span>
                        <span className={styles.sendOptionDescription}>
                          Send a copy of the quote directly to the client's email
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
